// Product thumbnail lookup for admin list screens (Product, Stock Balance, …).
// The deployed core/content service APIs don't expose image URLs yet, so this reuses
// the same-origin /store/catalog route (src/index.ts) that already reads
// content.product_media straight from the business DB for the storefront mini-app.
// Cached briefly since several list pages fetch it on every mount.
//
// matchPackshot is imported, not copied: the colour->packshot ladder is one rule and the
// product page, the mirror and every cart line have to run the same one (see its own note).
import { matchPackshot } from "../../helpers/product-media.js";
import { BoundedCache } from "./refdata.js";
const TTL_MS = 30_000;
// Keyed by scope, not a single slot: the POS picker asks for one branch's SELLING assortment
// while the admin list screens on the same page ask for the unfiltered one, and a shared slot
// would hand whichever landed first to both — a product hidden from POS would appear in the till
// grid, or vanish from an admin list, depending only on mount order.
const catalogCache = new Map();
// Raw rows, keyed the same way — see catalogRaw() for why the cache sits at this level.
const catalogRawCache = new Map();
// Bumped by invalidateCatalogCache() and appended to both fetches below. Clearing the in-memory
// cache alone is not enough: /store/catalog and /store/variant-media both answer with
// `Cache-Control: public, max-age=120`, so the browser would hand back the pre-write body.
//
// KEPT IN localStorage, not just memory. A module variable starts at 0 on every page load, so the
// first read after a reload asked for the plain URL — the very one the browser was still holding
// from before the write — and only a HARD refresh (which bypasses that store) showed the change.
// Stored, the reloaded tab and any tab opened later ask for the post-write URL. Shared across
// tabs on purpose.
const BUST_KEY = "brandos.catalogBust";
let mediaBust = (() => {
    try {
        return Number(globalThis.localStorage?.getItem(BUST_KEY)) || 0;
    }
    catch {
        return 0;
    }
})();
function scopeQuery(scope) {
    const q = new URLSearchParams();
    if (scope.branch)
        q.set("branch", scope.branch);
    if (scope.surface)
        q.set("surface", scope.surface);
    if (mediaBust)
        q.set("v", String(mediaBust));
    const s = q.toString();
    return s ? `?${s}` : "";
}
async function fetchCatalog(scope) {
    // Goes through catalogRaw so the mapped view and the raw readers share ONE request per scope.
    return (await catalogRaw(scope))
        .map((it) => ({
        materialNo: String(it?.materialNo ?? ""),
        brand: String(it?.brandCode ?? "").trim(),
        title: String(it?.descriptionEn || it?.descriptionTh || "").trim(),
        category: String(it?.categoryCode ?? ""),
        imageUrl: String(it?.imageUrl ?? ""),
        price: Number(it?.price ?? 0),
        branchQty: Number(it?.branchQty ?? 0),
        campaignPrice: it?.campaignPrice != null ? Number(it.campaignPrice) : null,
        campaignRef: it?.campaignRef ? String(it.campaignRef) : null,
        campaignLabel: it?.campaignLabel != null ? String(it.campaignLabel) : null,
        campaignOffLabel: String(it?.campaignOffLabel ?? ""),
        compareAt: it?.compareAt != null ? Number(it.compareAt) : null,
        carried: it?.carried === true,
        preorderable: it?.preorderable === true,
        preorderSupplement: it?.preorderSupplement === true,
        centralQty: Number(it?.centralQty ?? 0),
        barcodes: String(it?.barcodes ?? ""),
    }))
        .filter((it) => it.materialNo);
}
/**
 * The catalogue rows exactly as `/store/catalog` returns them, cached per scope.
 *
 * The cache sits on the RAW rows rather than on the mapped {@link CatalogItem}s because six staff
 * panes need fields the mapping drops (`descriptionEn`, `categoryCode`, …) and so were calling
 * `fetch("/store/catalog?branch=…")` directly, bypassing the cache entirely. On the flagship
 * terminal — where every pane mounts — that meant **13 fetches of the same catalogue** on one page
 * load. With the cache one level lower, those panes and {@link storeCatalog} share a single read.
 */
function catalogRaw(scope = {}) {
    const now = Date.now();
    const key = `${scope.branch ?? ""}|${scope.surface ?? ""}`;
    const hit = catalogRawCache.get(key);
    if (!hit || now - hit.at > TTL_MS) {
        const p = fetch("/store/catalog" + scopeQuery(scope))
            .then((r) => (r.ok ? r.json() : null))
            .then((j) => (Array.isArray(j?.items) ? j.items : []))
            .catch(() => []);
        catalogRawCache.set(key, { at: now, p });
        return p;
    }
    return hit.p;
}
function catalog(scope = {}) {
    const now = Date.now();
    const key = `${scope.branch ?? ""}|${scope.surface ?? ""}`;
    const hit = catalogCache.get(key);
    if (!hit || now - hit.at > TTL_MS) {
        const entry = { at: now, p: fetchCatalog(scope).catch(() => []) };
        catalogCache.set(key, entry);
        return entry.p;
    }
    return hit.p;
}
/** Raw catalogue rows for the panes that read fields {@link CatalogItem} discards. Shares one
 *  cached request per scope with everything else that reads the catalogue. */
export async function storeCatalogRaw(scope = {}) {
    return catalogRaw(scope);
}
/**
 * Drop the cached catalogue/variant reads after THIS tab wrote product media.
 *
 * The 30s TTL exists because several list screens mount at once and read the same rows; it is
 * wrong for the screen that just replaced a product's cover image, which would otherwise show
 * the old photo for up to half a minute and read as "the upload did nothing". Call it right
 * after a media write, before re-reading. (The server route it fetches sets its own
 * `Cache-Control: max-age=120`, so the re-fetch below deliberately cache-busts too.)
 */
export function invalidateCatalogCache() {
    // A timestamp, not +1: two tabs bumping from different starting points must not both land on
    // a number one of them already fetched under.
    mediaBust = Date.now();
    try {
        globalThis.localStorage?.setItem(BUST_KEY, String(mediaBust));
    }
    catch { /* private window */ }
    catalogCache.clear();
    // The raw slot too — it is the one the mapped view is derived from, so leaving it would serve
    // the pre-write rows straight back through catalog().
    catalogRawCache.clear();
    variantCache = null;
    materialFactsCache.clear();
}
/**
 * Store a binary asset and get back a same-origin URL for it.
 *
 * `POST /media/rich` (src/index.ts) takes a base64 data URL, keeps the bytes in
 * `brandos.rich_image`, and serves them back immutable for a year. Two kinds of asset need this
 * rather than an inline data URL in whatever record references them:
 *
 *  - rich-text images, which would otherwise ride along on every row of a 200-row list read;
 *  - brand FONTS, which cannot be inlined at all — CSP's `fontSrc` has no `data:`, so an
 *    `@font-face` with a data URL is blocked by the browser (Membership Setup › Typography).
 *
 * Throws with the server's own message, so a caller can put it straight in front of the user —
 * the endpoint distinguishes "not a type we accept", "too large" and "not actually a WOFF2".
 */
export async function uploadRichMedia(dataUrl) {
    const res = await fetch("/media/rich", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ dataUrl }),
    });
    const json = await res.json().catch(() => null);
    if (!res.ok || !json?.data?.url)
        throw new Error(json?.error?.message ?? `Upload failed (${res.status})`);
    return { id: String(json.data.id ?? ""), url: String(json.data.url) };
}
/** Full storefront catalog rows (materialNo, title, category, image, price, branch stock) —
 *  same cached fetch productImageMap()/productTitleMap() already use, but with the fields
 *  those two discard. Used by the POS catalog picker (pos-catalog.ts), which passes its branch
 *  and `surface: "POS"` so the till offers only what the product is merchandised to sell there. */
export async function storeCatalog(scope = {}) {
    return catalog(scope);
}
/** materialNo -> image URL, for every product that has a photo. */
/** materialNo -> category code, off the same cached catalogue rows the image map reads. Both
 *  stock screens print it as the second line of the product cell. */
export async function productCategoryMap() {
    const map = {};
    for (const it of await catalog())
        if (it.category)
            map[it.materialNo] = it.category;
    return map;
}
/** materialNo -> its brand code. Shares the one cached /store/catalog read with the maps above. */
export async function productBrandMap() {
    const map = {};
    for (const it of await catalog())
        if (it.brand)
            map[it.materialNo] = it.brand;
    return map;
}
export async function productImageMap() {
    const map = {};
    for (const it of await catalog())
        if (it.imageUrl)
            map[it.materialNo] = it.imageUrl;
    return map;
}
export function skuImage(vmedia, sku, cover) {
    const vm = vmedia?.byVariant?.[sku];
    if (vm?.packshotExact && vm.packshot)
        return vm.packshot;
    return singleColourway(vmedia, vm?.materialNo) ? String(cover ?? "") : "";
}
/** True when `materialNo` has at most one colourway — the only case a style-level photo is a SKU's. */
export function singleColourway(vmedia, materialNo) {
    if (!materialNo)
        return false;
    const n = vmedia?.colourways?.[materialNo];
    return n !== undefined && n <= 1;
}
let variantCache = null;
async function fetchVariants() {
    const res = await fetch("/store/variant-media" + (mediaBust ? `?v=${mediaBust}` : ""));
    if (!res.ok)
        return [];
    const json = (await res.json().catch(() => null));
    return (json?.items ?? [])
        .map((it) => ({
        variantSku: String(it?.variantSku ?? ""),
        materialNo: String(it?.materialNo ?? ""),
        colour: String(it?.colour ?? ""),
        size: String(it?.size ?? ""),
        specName: String(it?.specName ?? ""),
        packshot: String(it?.packshotUrl ?? ""),
        packshotExact: it?.packshotExact === true,
        status: String(it?.status ?? ""),
    }))
        .filter((it) => it.variantSku);
}
function variants() {
    const now = Date.now();
    if (!variantCache || now - variantCache.at > TTL_MS)
        variantCache = { at: now, p: fetchVariants().catch(() => []) };
    return variantCache.p;
}
// Bounded both ways (see BoundedCache): no TTL/cap here meant a picking screen priming a
// different mix of materials on every 10-15s poll (staffstockpick.ts) grew this by one entry per
// distinct material ever seen, for the life of the tab, and never refreshed a price or packshot
// once cached. 500 entries covers a picking session touching hundreds of SKUs across a shift;
// 30s matches this file's existing TTL_MS (catalogCache/variantCache, below).
const materialFactsCache = new BoundedCache(TTL_MS, 500);
/** Colour key shared with the apps: case/separator-insensitive ("Night Fall" == "night-fall"). */
export function colourKey(s) {
    return String(s ?? "").trim().toLowerCase().replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim();
}
/** The packshot for one line, resolving its colour by SKU first (see LineFacts.colourBySku) —
 *  a line captioned "Default" still lands on its own colourway's flat-lay. */
export function packshotForLine(facts, sku, colour) {
    if (!facts)
        return "";
    return pickPackshot(facts.packshots, facts.colourBySku[String(sku ?? "")] || colour, colourwaysOf(facts));
}
/** How many distinct colourways the product has, off the same facts the packshots came from —
 *  what tells pickPackshot whether one untagged flat-lay may stand for a colour it could not
 *  match. Counted over the VARIANTS, not the photos: a six-colourway product with one tagged
 *  packshot must not hand that photo to the other five. */
export function colourwaysOf(facts) {
    const seen = new Set();
    for (const c of Object.values(facts?.colourBySku ?? {})) {
        const k = colourKey(String(c ?? ""));
        if (k)
            seen.add(k);
    }
    return seen.size || 1;
}
/**
 * Which packshot a line showing `colour` may use — "" when none may be.
 *
 * A line prints its colour right next to the picture, so a wrong colourway is worse than the
 * model shot it already has. THE SAME LADDER the product page runs (matchPackshot in
 * helpers/product-media.ts) — called into rather than restated, because two copies of it drifting
 * is exactly how a cart line's picture and its product page's picture come to disagree. See that
 * function for why the comparison is by ordered word prefix and not by word set.
 */
export function pickPackshot(map, colour, colourways) {
    const packs = Object.entries(map).map(([c, url]) => ({ colour: colourKey(c), url }));
    return matchPackshot(packs, colour, { colourways: colourways ?? 1 }).url;
}
export function materialLineFacts(materialNo, branch) {
    // Keyed by branch as well: a campaign is scoped to a branch (and the storefront asks as its
    // own), so the same product answers 490 at one shop and 49 at another. Sharing one cache slot
    // across branches would hand a shopper the other shop's price.
    const mat = String(materialNo ?? "");
    const key = mat + "|" + (branch ?? "");
    let hit = materialFactsCache.get(key);
    if (!hit) {
        hit = fetch("/store/product?material_no=" + encodeURIComponent(mat) + (branch ? "&branch=" + encodeURIComponent(branch) : ""))
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => {
            const packshots = {};
            for (const m of (d?.media ?? [])) {
                if (String(m?.role ?? "") !== "packshot")
                    continue;
                const c = colourKey(String(m?.colour ?? ""));
                const url = String(m?.url ?? "");
                if (c && url && !packshots[c])
                    packshots[c] = url; // first packshot per colour wins
            }
            const campaignBySku = {};
            const colourBySku = {};
            const priceBySku = {};
            for (const v of (d?.variants ?? [])) {
                const sku = String(v?.sku ?? "");
                if (!sku)
                    continue;
                const camp = Number(v?.campaignPrice ?? 0);
                const base = Number(v?.price ?? 0);
                if (camp > 0 && base > 0 && camp < base)
                    campaignBySku[sku] = camp;
                if (base > 0)
                    priceBySku[sku] = base;
                const colour = String(v?.colour ?? "").trim();
                if (colour)
                    colourBySku[sku] = colour;
            }
            return { packshots, campaignBySku, colourBySku, priceBySku };
        })
            .catch(() => ({ packshots: {}, campaignBySku: {}, colourBySku: {}, priceBySku: {} }));
        materialFactsCache.set(key, hit);
    }
    return hit;
}
/** Just the colour→packshot half, for callers that only draw pictures. */
export function materialPackshots(materialNo, branch) {
    return materialLineFacts(materialNo, branch).then((f) => f.packshots);
}
/**
 * materialNo -> (colour -> packshot) for a whole SCREEN of order lines, one cached read per
 * distinct material. For the rails that render somebody else's basket — the picking list and the
 * stockroom pick — where a line arrives with whatever photo the selling surface stored on it, or
 * with none at all and a catalogue COVER (a model shot) joined in its place. The picker is
 * matching a physical piece against a picture, so the flat-lay is the useful one.
 *
 * Pass every line's materialNo; use `pickPackshot(index[materialNo], colour)` per line so a
 * colour with no flat-lay of its own keeps the photo it already had.
 */
export async function packshotIndex(materialNos, branch) {
    const wanted = [...new Set(materialNos.map((m) => String(m ?? "")).filter(Boolean))];
    await primeLineFacts(wanted, branch);
    const out = {};
    await Promise.all(wanted.map(async (m) => { out[m] = await materialLineFacts(m, branch); }));
    return out;
}
/**
 * Fetch many materials' line facts in ONE request and seed the per-material cache with them.
 *
 * This used to be `Promise.all(materials.map(materialLineFacts))` — parallel, but still one
 * `/store/product` round trip per material, each waiting on six Hub reads behind it. A picking
 * screen with forty lines therefore opened forty requests before it could draw a single thumbnail,
 * and that is most of the ~15s the flagship terminal took to paint. `/store/product-facts` answers
 * the same shape for the whole list at once (src/index.ts), so the screen pays one round trip.
 *
 * It seeds `materialFactsCache` rather than returning the map, so every existing
 * `materialLineFacts()` call site — the mini-app cart, staff Assist — gets the batched answer
 * without changing how it asks. Materials the batch did not answer for are left uncached and fall
 * back to their own single read.
 */
export function primeLineFacts(materialNos, branch) {
    const key = (m) => m + "|" + (branch ?? "");
    const missing = [...new Set(materialNos.map((m) => String(m ?? "")).filter(Boolean))]
        .filter((m) => !materialFactsCache.has(key(m)));
    if (!missing.length)
        return Promise.resolve();
    const url = "/store/product-facts?material_no=" + encodeURIComponent(missing.join(","))
        + (branch ? "&branch=" + encodeURIComponent(branch) : "");
    const batch = fetch(url)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => (d?.facts ?? null))
        .catch(() => null);
    // Seed SYNCHRONOUSLY, before the batch resolves: each material gets a promise derived from the
    // one in-flight request. That is what lets a caller keep its existing per-line
    // `materialLineFacts()` loop — the loop runs in this same tick, finds a cache entry already
    // there, and joins the batch instead of opening its own request.
    const EMPTY = { packshots: {}, campaignBySku: {}, colourBySku: {}, priceBySku: {} };
    for (const m of missing) {
        const p = batch.then((facts) => {
            const f = facts?.[m];
            // Batch unreachable, or it had nothing for this material: fall back to the single read
            // rather than caching an empty answer, or the line would keep its wrong photo forever.
            if (!f) {
                materialFactsCache.delete(key(m));
                return materialLineFacts(m, branch);
            }
            return {
                packshots: f.packshots ?? {},
                campaignBySku: f.campaignBySku ?? {},
                colourBySku: f.colourBySku ?? {},
                // Defaulted, not required: a server running ahead of this bundle answers without
                // it, and a missing tag price only costs a gift line its shelf value.
                priceBySku: f.priceBySku ?? {},
            };
        }).catch(() => EMPTY);
        materialFactsCache.set(key(m), p);
    }
    return batch.then(() => undefined);
}
/**
 * variantSku -> materialNo, from the same cached catalogue feed the media maps read.
 *
 * `variantMediaMap()` throws the material link away (it only keeps it to build the packshot
 * fallback), but a caller that POSTS about a SKU needs it: `inventory.stock_movement.material_no`
 * is NOT NULL, and the service's own fallback resolves it from `inventory.material_variant_replica`
 * — which is populated for MSK and EMPTY for LBLB, so an omitted material_no there lands as the
 * variant code itself and opens a second, wrong stock_balance grain beside the real row.
 */
export async function variantMaterialMap() {
    const byVariant = {};
    for (const it of await variants()) {
        if (it.materialNo)
            byVariant[it.variantSku] = it.materialNo;
    }
    return byVariant;
}
/** variantSku -> { size, colour, packshot }, plus a materialNo -> packshot fallback.
 *  A variant's `packshot` is its OWN colourway's flat-lay or "" — never the style's first one,
 *  because every screen that reads it prints that SKU beside the picture. `byMaterial` is the
 *  style-level photo for a row that names no colour; a variant row must not fall back to it. */
export async function variantMediaMap() {
    const byVariant = {};
    const byMaterial = {};
    const ways = {};
    for (const it of await variants()) {
        byVariant[it.variantSku] = { size: it.size, colour: it.colour, specName: it.specName, packshot: it.packshotExact ? it.packshot : "", packshotExact: it.packshotExact, status: it.status, materialNo: it.materialNo };
        // First packshot seen per material becomes the material-level fallback.
        if (it.materialNo && it.packshot && !byMaterial[it.materialNo])
            byMaterial[it.materialNo] = it.packshot;
        if (it.materialNo)
            (ways[it.materialNo] ??= new Set()).add(String(it.colour ?? "").trim().toLowerCase());
    }
    const colourways = {};
    for (const [m, s] of Object.entries(ways))
        colourways[m] = s.size;
    return { byVariant, byMaterial, colourways };
}
/** variantSku -> { materialNo, colour, size } for every variant that has media.
 *  variantMediaMap() drops materialNo and materialColourMap() drops the variant key, so neither
 *  answers "which material, colour and size is this SKU?" — the join campaign-detail's per-variant
 *  sales drill-down needs to turn a promo usage event's raw `sku` into a product row. */
export async function variantIndex() {
    const out = {};
    for (const it of await variants()) {
        out[it.variantSku] = { materialNo: it.materialNo, colour: it.colour, size: it.size };
    }
    return out;
}
/**
 * Resolve a human product title from WHATEVER identifier a list row carries — the storefront
 * materialNo ("slug", e.g. "ace-track-pants") OR a variant SKU (materialVariantCode / itemCode,
 * e.g. "KAA0197IV-O"). Warehouse/movement rows are inconsistent (some carry the slug, some the
 * SKU), so the returned map is keyed by BOTH: look up `map[row.materialNo] || map[row.itemCode]
 * || map[row.sku]` and fall back to the raw code when unknown. Titles come from /store/catalog
 * (keyed on the slug the balance/warehouse rows actually use — core/catalog/material/list is keyed
 * on the real material code and does not join here).
 */
export async function productTitleMap() {
    const [items, vs] = await Promise.all([catalog(), variants()]);
    const map = {};
    for (const it of items)
        if (it.title)
            map[it.materialNo] = it.title; // slug -> title
    for (const v of vs) { // variant SKU -> its OWN name
        // The variant's `specification_name` beats its material's title on a variant key, and
        // this is the one place to say so: every inventory pane resolves its row label through
        // this map, and a worklist/ledger/task row is one line PER SKU. With the material title
        // alone, three sizes of one style printed the same words and only the code told them
        // apart. Falls back to the material title, so a tenant that has not filled the column
        // (LBLB, CKN) sees exactly what it saw before.
        //
        // Material keys keep the material title: a row at that grain is about the style, not a SKU.
        const title = v.specName || map[v.materialNo];
        if (v.variantSku && title && !map[v.variantSku])
            map[v.variantSku] = title;
    }
    return map;
}
//# sourceMappingURL=media.js.map